/**
 * Utility functions for text marking and highlighting
 */

import { MarkedRange } from '../types/training';
import { v4 as uuidv4 } from 'uuid';

/**
 * Add a new text mark/highlight to a file
 * @param marks - Existing marks array
 * @param start - Start position in text
 * @param end - End position in text
 * @param color - Color for the highlight
 * @returns Updated marks array
 */
export const addTextMark = (
  marks: MarkedRange[] | undefined,
  start: number,
  end: number,
  color: string
): MarkedRange[] => {
  const newMarks = marks || [];
  const newMark: MarkedRange = {
    id: uuidv4(),
    start,
    end,
    color,
    createdAt: Date.now(),
  };
  return [...newMarks, newMark];
};

/**
 * Remove a mark by ID
 * @param marks - Marks array
 * @param markId - ID of mark to remove
 * @returns Updated marks array
 */
export const removeTextMark = (
  marks: MarkedRange[] | undefined,
  markId: string
): MarkedRange[] => {
  if (!marks) return [];
  return marks.filter((mark) => mark.id !== markId);
};

/**
 * Clear all marks
 * @returns Empty marks array
 */
export const clearAllMarks = (): MarkedRange[] => {
  return [];
};

/**
 * Get text with highlighting applied
 * Returns array of { text, isMarked, markId, color } segments
 */
export const getMarkedSegments = (
  text: string,
  marks: MarkedRange[] | undefined
): Array<{
  text: string;
  isMarked: boolean;
  markId?: string;
  color?: string;
  start: number;
  end: number;
}> => {
  if (!marks || marks.length === 0) {
    return [{ text, isMarked: false, start: 0, end: text.length }];
  }

  // Sort marks by start position
  const sortedMarks = [...marks].sort((a, b) => a.start - b.start);

  const segments: Array<{
    text: string;
    isMarked: boolean;
    markId?: string;
    color?: string;
    start: number;
    end: number;
  }> = [];

  let currentPos = 0;

  for (const mark of sortedMarks) {
    // Add unmarked text before this mark
    if (currentPos < mark.start) {
      segments.push({
        text: text.substring(currentPos, mark.start),
        isMarked: false,
        start: currentPos,
        end: mark.start,
      });
    }

    // Add marked text
    segments.push({
      text: text.substring(mark.start, mark.end),
      isMarked: true,
      markId: mark.id,
      color: mark.color,
      start: mark.start,
      end: mark.end,
    });

    currentPos = mark.end;
  }

  // Add remaining unmarked text
  if (currentPos < text.length) {
    segments.push({
      text: text.substring(currentPos),
      isMarked: false,
      start: currentPos,
      end: text.length,
    });
  }

  return segments;
};

/**
 * Serialize marks to JSON string
 */
export const serializeMarks = (marks: MarkedRange[] | undefined): string => {
  return JSON.stringify(marks || []);
};

/**
 * Deserialize marks from JSON string
 */
export const deserializeMarks = (marksJson: string): MarkedRange[] => {
  try {
    return JSON.parse(marksJson);
  } catch {
    return [];
  }
};

/**
 * Check if text selection overlaps with existing marks
 */
export const hasOverlapingMarks = (
  marks: MarkedRange[] | undefined,
  start: number,
  end: number
): boolean => {
  if (!marks) return false;
  return marks.some((mark) => {
    return !(mark.end <= start || mark.start >= end);
  });
};

/**
 * Get mark at specific position in text
 */
export const getMarkAtPosition = (
  marks: MarkedRange[] | undefined,
  position: number
): MarkedRange | undefined => {
  if (!marks) return undefined;
  return marks.find((mark) => position >= mark.start && position < mark.end);
};
